import { Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { BaseOptionType } from "antd/lib/select"
import { type TFunction } from "i18next"
import React from "react"

import { type NamePath, type TabDef } from "../../../../components/Forms/TabbedEditForm/tab-types.ts"
import { type Project } from "../../types/project.types.ts"
import { ProjectSectionCard } from "./ProjectSectionCard.tsx"
import { type CustomerPick, type ProjectFormData } from "./project-form-data.ts"
import { AttachmentsSection } from "./sections/AttachmentsSection.tsx"
import { BudgetSummarySection } from "./sections/BudgetSummarySection.tsx"
import { CustomerAddressSection } from "./sections/CustomerAddressSection.tsx"
import { CustomerContactSection } from "./sections/CustomerContactSection.tsx"
import { MilestonesSetupSection } from "./sections/MilestonesSetupSection/MilestonesSetupSection.tsx"
import { ProjectDetailsSection } from "./sections/ProjectDetailsSection/ProjectDetailsSection.tsx"
import { PropertyInformationSection } from "./sections/PropertyInformationSection.tsx"
import { SourceSection } from "./sections/SourceSection.tsx"
import { StakeholdersAndRolesSection } from "./sections/StakeholdersAndRolesSection/StakeholdersAndRolesSection.tsx"
import { TextArea } from "../../../../components/Input/TextArea.tsx"

// Lists every field the tabs render. A rendered field missing here gets no error
// badge and no submit-jump (its validation error routes to no tab). Keep in sync.
// Customer fields are nested paths so contact fields (Overview) and address fields
// (Address tab) route to different tabs — matching is prefix-based.
export const PROJECT_TAB_FIELDS = {
  overview: [
    "source", "source_comment", "architect_name", "branch", "property_ownership", "preferred_contact_method",
    "lead_name", "project_type", "start_date", "end_date", "comments",
    ["customer", "email"], ["customer", "first_name"], ["customer", "middle_name"],
    ["customer", "last_name"], ["customer", "phone"], ["customer", "phone_2"],
    ["customer", "phone_3"], "temporary_customer",
    "attachments", "file_list", "internal_notes"
  ],
  customer: [
    ["customer", "address"], ["customer", "address_2"], ["customer", "city"],
    ["customer", "state"], ["customer", "zip_code"],
    "address", "city", "state", "zip_code", "address_2", "use_customer_address"
  ],
  scope: ["project_phases"],
  financial: [
    "sales_price",
    "planned_payable",
    "margin",
    "margin_multiplier",
    "margin_warning_percent"
  ],
  activity: ["project_managers", "sales_manager"]
} satisfies Record<string, NamePath[]>

interface BuildProjectTabsArgs {
  t: TFunction
  form: FormInstance<ProjectFormData>
  options: Record<string, BaseOptionType[]>
  isEdit?: boolean
  saving?: boolean
  initialValues?: Partial<ProjectFormData>
  customerPick?: CustomerPick | null
  onCustomerPicked?: (zip: string | null) => void
  projectFull?: Project
  assignedSalesAllowed?: boolean
  assignedPMAllowed?: boolean
  financeAllowed?: boolean
}

export function buildProjectTabs({
  t, form, options, isEdit, saving, initialValues, projectFull, assignedSalesAllowed, assignedPMAllowed, financeAllowed,
  customerPick, onCustomerPicked
}: BuildProjectTabsArgs): TabDef[] {
  const showScope = !isEdit

  const tabs: TabDef[] = []

  // On Edit, every section this tab used to hold now has its own card:
  // Customer Information / Source / Project Details / Description moved
  // first, and Attachments & Notes (the tab's last remaining content) moved
  // to the Files and Internal Notes cards — a second, disconnected editor
  // for the same fields would just create two save paths for one value.
  if (!isEdit) {
    tabs.push({
      key: "overview", label: t("tabbedForm.tabs.overview") as string, fields: PROJECT_TAB_FIELDS.overview,
      render: () => (
        <>
          <ProjectSectionCard title={t("tabbedForm.sections.customerInformation") as string}>
            <CustomerContactSection form={form} saving={saving} options={options}
              onCustomerPicked={onCustomerPicked} />
          </ProjectSectionCard>
          <ProjectSectionCard>
            <SourceSection form={form} options={options} />
          </ProjectSectionCard>
          <ProjectSectionCard title={t("tabbedForm.sections.projectDetails") as string}>
            <ProjectDetailsSection form={form} isEdit={isEdit} options={options}
              projectTypeDetails={projectFull?.project_type_details || []} />
          </ProjectSectionCard>
          <ProjectSectionCard title={t("tabbedForm.sections.description") as string}>
            <Form.Item<ProjectFormData> name="comments" className="mb-0">
              <TextArea
                aria-label={t("tabbedForm.sections.description") as string}
                placeholder={t("tabbedForm.fields.descriptionPlaceholder") as string}
                rows={6}
              />
            </Form.Item>
          </ProjectSectionCard>
          <ProjectSectionCard title={t("tabbedForm.sections.attachmentsAndNotes") as string}>
            <AttachmentsSection initialValues={initialValues} form={form} />
            <Form.Item<ProjectFormData> label={t("tabbedForm.fields.internalNotes") as string} name="internal_notes">
              <TextArea placeholder={t("tabbedForm.fields.internalNotesPlaceholder") as string} rows={4} />
            </Form.Item>
          </ProjectSectionCard>
        </>
      )
    })
  }

  // On Edit, the Location card now fully replaces this tab's Customer
  // Address + Property Information sections — a second, disconnected way
  // to edit the same address fields would be exactly the duplicate-save-path
  // problem the Overview tab's cards already avoid. Add doesn't have that
  // card yet, so it keeps its own Address tab.
  if (!isEdit) {
    tabs.push({
      key: "customer", label: t("tabbedForm.tabs.address") as string, fields: PROJECT_TAB_FIELDS.customer,
      render: () => (
        <>
          <ProjectSectionCard title={t("tabbedForm.sections.customerAddress") as string}>
            <CustomerAddressSection form={form} saving={saving} options={options}
              initialZip={initialValues?.customer?.zip_code}
              customerPick={customerPick} />
          </ProjectSectionCard>
          <ProjectSectionCard title={t("tabbedForm.sections.propertyInformation") as string}>
            <PropertyInformationSection form={form} options={options}
              initialZip={initialValues?.zip_code} />
          </ProjectSectionCard>
        </>
      )
    })
  }

  if (showScope) {
    tabs.push({
      key: "scope", label: t("tabbedForm.tabs.scope") as string, fields: PROJECT_TAB_FIELDS.scope,
      render: () => (
        <ProjectSectionCard>
          <MilestonesSetupSection form={form} />
        </ProjectSectionCard>
      )
    })
  }

  // On Edit, the Financial Summary card now fully replaces this tab — same
  // Sales Price/Planned Payable/margin settings, same disconnected-second-
  // editor problem the Overview/Address/Activity tabs already avoided.
  // Add doesn't have that card yet, so it keeps its own Financial tab.
  if (financeAllowed && !isEdit) {
    tabs.push({
      key: "financial", label: t("tabbedForm.tabs.financial") as string, fields: PROJECT_TAB_FIELDS.financial,
      render: () => (
        <ProjectSectionCard title={t("tabbedForm.sections.financialSummary") as string}>
          <BudgetSummarySection />
        </ProjectSectionCard>
      )
    })
  }

  // On Edit, the Activity card now fully replaces this tab — same Project
  // Manager / Sales Manager assignment, same disconnected-second-editor
  // problem the Overview/Address tabs already avoided. Add doesn't have
  // that card yet, so it keeps its own Activity tab.
  if (!isEdit) {
    tabs.push(
      {
        key: "activity", label: t("tabbedForm.tabs.activity") as string, fields: PROJECT_TAB_FIELDS.activity,
        render: () => (
          <ProjectSectionCard title={t("tabbedForm.sections.stakeholdersAndRoles") as string}>
            <StakeholdersAndRolesSection options={options} assignedSalesAllowed={assignedSalesAllowed} assignedPMAllowed={assignedPMAllowed} assignedProjectManagers={projectFull?.project_managers} assignedSalesManagers={projectFull?.sales_manager} />
          </ProjectSectionCard>
        )
      }
    )
  }

  return tabs
}
